// src/pages/school/fees/FeesCollect.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Container, Row, Col, Card, Form, InputGroup, Button,
  Table, Spinner, Badge, Alert
} from "react-bootstrap";
import {
  collection, query, where, orderBy, limit,
  startAt, endAt, getDocs, doc,
  runTransaction, serverTimestamp
} from "firebase/firestore";
import { db } from "../../../services/firebase";
import useSchool from "../../../hooks/useSchool";
import { useAuth } from "../../../context/AuthContext";
import Swal from "sweetalert2";

/* ---------------- helpers ---------------- */
const fmtINR = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 })
    .format(Number(n || 0));

/** Strip undefined & NaN (Firestore rejects both) */
const cleanDoc = (obj) =>
  Object.fromEntries(
    Object.entries(obj).filter(
      ([, v]) => v !== undefined && !(typeof v === "number" && Number.isNaN(v))
    )
  );

/** yyyy-mm-dd for reporting buckets */
const ymd = (d = new Date()) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

/* ---------- Firestore refs ---------- */
const studentsRef     = (sid) => collection(db, "schools", sid, "students");
const feesFixedRef    = (sid) => collection(db, "schools", sid, "fees_fixed");
const feesDuesRef     = (sid) => collection(db, "schools", sid, "fees_dues");
const feesPaymentsRef = (sid) => collection(db, "schools", sid, "fees_payments");

/* ---------- Debounced Student Lookup (nameLower/name/roll/phone) ---------- */
const DEBOUNCE_MS = 250;
function StudentLookup({ schoolId, classFilter, sectionFilter, onPick }) {
  const [qText, setQText] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);
  const timer = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (!boxRef.current?.contains(e.target)) setOpen(false); };
    document.addEventListener("click", onDoc);
    return () => document.removeEventListener("click", onDoc);
  }, []);

  useEffect(() => {
    if (!schoolId) return;
    if (timer.current) clearTimeout(timer.current);

    timer.current = setTimeout(async () => {
      const qtxt = qText.trim().toLowerCase();
      if (!qtxt) { setRows([]); return; }
      setLoading(true);
      try {
        const base = studentsRef(schoolId);
        const cls = classFilter ? [where("className", "==", String(classFilter))] : [];
        const sec = sectionFilter ? [where("section", "==", String(sectionFilter))] : [];

        // Try nameLower, then fallback to name
        const tryQueries = [
          query(base, ...cls, ...sec, orderBy("nameLower"), startAt(qtxt), endAt(qtxt + "\uf8ff"), limit(12)),
          query(base, ...cls, ...sec, orderBy("name"),      startAt(qtxt), endAt(qtxt + "\uf8ff"), limit(12)),
        ];

        const acc = new Map();
        for (const qu of tryQueries) {
          try {
            const snap = await getDocs(qu);
            snap.docs.forEach(d => acc.set(d.id, { id: d.id, ...d.data() }));
            if (acc.size) break;
          } catch {/* ignore (missing index/field) */}
        }

        if (/^\d+/.test(qtxt)) {
          try {
            const s = await getDocs(query(base, ...cls, ...sec, orderBy("rollNo"), startAt(qtxt), endAt(qtxt + "\uf8ff"), limit(8)));
            s.docs.forEach(d => acc.set(d.id, { id: d.id, ...d.data() }));
          } catch {}
        }
        if (/^\d{3,}$/.test(qtxt)) {
          try {
            const s = await getDocs(query(base, ...cls, ...sec, orderBy("phone"), startAt(qtxt), endAt(qtxt + "\uf8ff"), limit(8)));
            s.docs.forEach(d => acc.set(d.id, { id: d.id, ...d.data() }));
          } catch {}
        }

        setRows(Array.from(acc.values()).slice(0, 20));
        setOpen(true);
      } finally {
        setLoading(false);
      }
    }, DEBOUNCE_MS);
  }, [qText, schoolId, classFilter, sectionFilter]);

  return (
    <div ref={boxRef} style={{ position: "relative" }}>
      <Form.Label>Student</Form.Label>
      <InputGroup>
        <InputGroup.Text><i className="bi bi-search" /></InputGroup.Text>
        <Form.Control
          placeholder="Type name, roll no, or phone…"
          value={qText}
          onChange={(e) => setQText(e.target.value)}
          onFocus={() => rows.length && setOpen(true)}
        />
        {loading && <InputGroup.Text><Spinner size="sm" animation="border" /></InputGroup.Text>}
      </InputGroup>

      {open && rows.length > 0 && (
        <div className="shadow border bg-white rounded-3 mt-1"
             style={{ position: "absolute", zIndex: 30, left: 0, right: 0, maxHeight: 280, overflowY: "auto" }}>
          {rows.map(st => (
            <button key={st.id}
              className="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
              onClick={() => { onPick(st); setOpen(false); }}>
              <div className="text-start">
                <div className="fw-semibold">{st.name || st.displayName || "Unnamed"}</div>
                <div className="small text-muted">
                  {st.className ? `Class ${st.className}${st.section ? `-${st.section}` : ""} · ` : ""}
                  {st.rollNo ? `Roll ${st.rollNo} · ` : ""}
                  {st.phone || st.email || st.id}
                </div>
              </div>
              {st.section && <Badge bg="light" text="dark">{st.section}</Badge>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------- Sync: mirror active fees_fixed -> fees_dues (one due per fixed row) ---------- */
async function syncFixedFeesToDues(schoolId, studentId) {
  // Active fixed-fee rows for this student
  const qFixed = query(
    feesFixedRef(schoolId),
    where("studentId", "==", studentId),
    where("status", "==", "active")
  );
  const fixedSnap = await getDocs(qFixed);
  const fixedList = fixedSnap.docs.map(d => ({ id: d.id, ...d.data() }));

  if (!fixedList.length) return;

  // Existing dues for this student to avoid duplicates
  const qDues = query(
    feesDuesRef(schoolId),
    where("studentId", "==", studentId)
  );
  const duesSnap = await getDocs(qDues);
  const existing = new Map(
    duesSnap.docs.map(d => {
      const data = d.data();
      return [data.fixedRefId || "", { id: d.id, ...data }];
    })
  );

  const creates = [];
  for (const fx of fixedList) {
    const net = Math.max(0, Number(fx.fixedFee || 0) - Number(fx.concession || 0));
    if (!net) continue;

    // If a due linked to this fixed row already exists, skip creation
    if (existing.has(fx.id)) continue;

    creates.push({
      schoolId, // helpful for reports
      studentId,
      categoryId: fx.categoryId || "",
      categoryName: fx.categoryName || "",
      amount: net,
      paidAmount: 0,
      status: "unpaid",
      source: "fixed",
      fixedRefId: fx.id,
      className: fx.className || "",
      section: fx.section || "",
      // You can set a dueDate if you want a target: undefined means "open"
      // dueDate: serverTimestamp(),
      createdAt: serverTimestamp(),
    });
  }

  if (creates.length) {
    // write each due in its own small transaction
    await Promise.all(
      creates.map(async (row) => {
        const payload = cleanDoc(row);
        const ref = doc(feesDuesRef(schoolId));
        await runTransaction(db, async (tx) => tx.set(ref, payload));
      })
    );
  }
}

/* ---------- Transaction: collect payment with auto-allocation ---------- */
async function collectPaymentTx({
  schoolId,
  student,            // { id, name, className, section, rollNo }
  selectedDueIds,     // string[]
  grossAmount,        // number
  discount = 0,       // number
  lateFee = 0,        // number
  mode,               // "cash" | "upi" | "card" | "bank" | "cheque"
  modeMeta = {},      // { txnRef?, bank?, chequeNo? }
  note = "",
}) {
  const netAmount = Math.max(0, Number(grossAmount || 0) + Number(lateFee || 0) - Number(discount || 0));
  if (netAmount <= 0) throw new Error("Net amount must be > 0");

  const duesColl = feesDuesRef(schoolId);
  const paysColl = feesPaymentsRef(schoolId);

  return runTransaction(db, async (tx) => {
    // 1) Load dues in tx
    const dueDocs = [];
    for (const id of selectedDueIds) {
      const ref = doc(duesColl, id);
      const snap = await tx.get(ref);
      if (!snap.exists()) throw new Error("Due not found: " + id);
      const d = { id: snap.id, ...snap.data(), _ref: ref };
      if (d.studentId !== student.id) throw new Error("Student mismatch for due: " + id);
      dueDocs.push(d);
    }

    // 2) Sort dues oldest-first (by dueDate, then createdAt)
    dueDocs.sort((a, b) => {
      const ad = a.dueDate?.toDate?.() || a.dueDate || a.createdAt?.toDate?.() || 0;
      const bd = b.dueDate?.toDate?.() || b.dueDate || b.createdAt?.toDate?.() || 0;
      return +ad - +bd;
    });

    // 3) Allocate
    let remaining = netAmount;
    const allocations = [];
    for (const d of dueDocs) {
      const amt = Number(d.amount || 0);
      const paid = Number(d.paidAmount || 0);
      const bal = Math.max(0, amt - paid);
      if (bal <= 0) continue;
      const pay = Math.min(remaining, bal);
      if (pay <= 0) break;

      const after = paid + pay;
      const status = after >= amt ? "paid" : after > 0 ? "partial" : "unpaid";

      allocations.push({
        dueId: d.id,
        categoryId: d.categoryId || null,
        before: paid,
        pay,
        after,
      });

      tx.update(d._ref, { paidAmount: after, status });
      remaining -= pay;
    }

    if (allocations.length === 0) throw new Error("Nothing to allocate (selected dues are fully paid).");

    // 4) Build payment record (report-friendly)
    const trimmedMeta = {};
    if (modeMeta?.txnRef && String(modeMeta.txnRef).trim())   trimmedMeta.txnRef = String(modeMeta.txnRef).trim();
    if (modeMeta?.bank && String(modeMeta.bank).trim())       trimmedMeta.bank   = String(modeMeta.bank).trim();
    if (modeMeta?.chequeNo && String(modeMeta.chequeNo).trim()) trimmedMeta.chequeNo = String(modeMeta.chequeNo).trim();

    const now = new Date();
    const basePayload = {
      schoolId,
      studentId: student.id,
      studentName: student.name || "",
      className: student.className || "",
      section: student.section || "",
      rollNo: student.rollNo || "",

      amount: Number(grossAmount || 0),
      discountAmount: Number(discount || 0) || undefined,
      lateFeeAmount: Number(lateFee || 0) || undefined,
      netAmount,

      mode,
      ...trimmedMeta,

      allocations,              // [{dueId,categoryId,before,pay,after}]
      note: (note || "").trim() || undefined,

      // reporting buckets
      paidOnYMD: ymd(now),
      paidYear: now.getFullYear(),
      paidMonth: now.getMonth() + 1, // 1..12

      receiptCode: `RCPT-${now.getTime()}`, // simple unique code for receipts
      createdAt: serverTimestamp(),
      paidAt: serverTimestamp(),
    };

    const payload = cleanDoc(basePayload);
    const payRef = doc(paysColl);
    tx.set(payRef, payload);

    return { paymentId: payRef.id, allocations, netAmount, unallocated: remaining };
  });
}

/* ====================================================================== */

export default function FeesCollect() {
  const { school } = useSchool();
  const { isSchoolAdmin } = useAuth();
  const schoolId = school?.id || null;

  // Student selection
  const [classFilter, setClassFilter] = useState("");
  const [sectionFilter, setSectionFilter] = useState("");
  const [student, setStudent] = useState(null);

  // Dues for student
  const [dues, setDues] = useState([]);
  const [loadingDues, setLoadingDues] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Payment form
  const [amount, setAmount] = useState("");
  const [discount, setDiscount] = useState("");
  const [lateFee, setLateFee] = useState("");
  const [mode, setMode] = useState("cash");
  const [txnRef, setTxnRef] = useState("");
  const [bank, setBank] = useState("");
  const [chequeNo, setChequeNo] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [autoAllocate, setAutoAllocate] = useState(true); // UI only (always on)

  // Load dues when student changes (includes fixed->dues sync)
  useEffect(() => {
    (async () => {
      setDues([]); setSelectedIds(new Set());
      if (!schoolId || !student?.id) return;
      setLoadingDues(true);
      try {
        // Ensure fixed-fee rows are mirrored into dues
        await syncFixedFeesToDues(schoolId, student.id);

        // Load dues
        const qy = query(
          feesDuesRef(schoolId),
          where("studentId", "==", student.id),
          orderBy("dueDate", "asc")
        );
        const snap = await getDocs(qy);
        const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        list.sort((a, b) => {
          const ad = a.dueDate?.toDate?.() || a.dueDate || a.createdAt?.toDate?.() || 0;
          const bd = b.dueDate?.toDate?.() || b.dueDate || b.createdAt?.toDate?.() || 0;
          return +ad - +bd;
        });
        setDues(list);
      } finally {
        setLoadingDues(false);
      }
    })();
  }, [schoolId, student?.id]);

  // Totals
  const totals = useMemo(() => {
    const sel = new Set(selectedIds);
    let totalAmt = 0, totalPaid = 0, totalBalSel = 0, totalBalAll = 0;
    for (const d of dues) {
      const amt = Number(d.amount || 0);
      const paid = Number(d.paidAmount || 0);
      const bal = Math.max(0, amt - paid);
      totalAmt += amt; totalPaid += paid; totalBalAll += bal;
      if (sel.has(d.id)) totalBalSel += bal;
    }
    const gross = Number(amount || 0);
    const disc  = Number(discount || 0);
    const late  = Number(lateFee || 0);
    const net   = Math.max(0, gross + late - disc);
    return { totalAmt, totalPaid, totalBalAll, totalBalSel, net };
  }, [dues, selectedIds, amount, discount, lateFee]);

  const setAllSelectable = () => {
    const sel = new Set();
    for (const d of dues) {
      const amt = Number(d.amount || 0);
      const paid = Number(d.paidAmount || 0);
      if (amt - paid > 0) sel.add(d.id);
    }
    setSelectedIds(sel);
  };
  const clearSelection = () => setSelectedIds(new Set());

  const quickSet = (type) => {
    const bal = totals.totalBalSel || 0;
    if (type === "full") setAmount(String(bal));
    if (type === "half") setAmount(String(Math.ceil(bal / 2)));
    if (type === "zero") setAmount("0");
  };

  const onCollect = async () => {
    if (!isSchoolAdmin) return Swal.fire({ icon: "warning", title: "Restricted", text: "Only School Admins can collect fees." });
    if (!schoolId || !student?.id) return Swal.fire({ icon: "warning", title: "Pick a student first" });

    const ids = Array.from(selectedIds);
    if (ids.length === 0) return Swal.fire({ icon: "warning", title: "Select at least one due" });

    const gross = Number(amount || 0);
    const disc  = Number(discount || 0);
    const late  = Number(lateFee || 0);
    const net   = Math.max(0, gross + late - disc);
    if (!gross || net <= 0) return Swal.fire({ icon: "warning", title: "Enter a valid amount" });

    const modeMeta = {};
    if ((mode === "upi" || mode === "card" || mode === "bank") && txnRef.trim()) modeMeta.txnRef = txnRef.trim();
    if ((mode === "bank" || mode === "cheque") && bank.trim()) modeMeta.bank = bank.trim();
    if (mode === "cheque" && chequeNo.trim()) modeMeta.chequeNo = chequeNo.trim();

    setSubmitting(true);
    try {
      const res = await collectPaymentTx({
        schoolId,
        student: {
          id: student.id,
          name: student.name || student.displayName || "",
          className: student.className || "",
          section: student.section || "",
          rollNo: student.rollNo || "",
        },
        selectedDueIds: ids,
        grossAmount: gross,
        discount: disc,
        lateFee: late,
        mode,
        modeMeta,
        note,
      });

      Swal.fire({
        icon: "success",
        title: "Payment recorded",
        html: `
          <div class="text-start">
            <div><b>Payment ID:</b> ${res.paymentId}</div>
            <div><b>Net amount:</b> ${fmtINR(res.netAmount)}</div>
            <div class="small text-muted mt-2">Allocations saved per due.</div>
          </div>
        `,
      });

      // refresh dues (balances & statuses updated in txn)
      const qy = query(feesDuesRef(schoolId), where("studentId", "==", student.id), orderBy("dueDate", "asc"));
      const snap = await getDocs(qy);
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      list.sort((a, b) => {
        const ad = a.dueDate?.toDate?.() || a.dueDate || a.createdAt?.toDate?.() || 0;
        const bd = b.dueDate?.toDate?.() || b.dueDate || b.createdAt?.toDate?.() || 0;
        return +ad - +bd;
      });
      setDues(list);

      // reset amount fields; keep selection
      setAmount(""); setDiscount(""); setLateFee(""); setTxnRef(""); setBank(""); setChequeNo(""); setNote("");
    } catch (e) {
      Swal.fire({ icon: "error", title: "Could not collect", text: e?.message || "Try again." });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container className="py-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="mb-0">Collect Fees</h5>
        <div className="text-muted small">{school?.name || "School"}</div>
      </div>

      <Row className="g-3">
        {/* LEFT: Student + Dues */}
        <Col lg={6}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Header className="bg-white border-0 fw-semibold">Student & Dues</Card.Header>
            <Card.Body>
              <Row className="g-2">
                <Col md={6}>
                  <Form.Label className="text-muted">Class (filter)</Form.Label>
                  <Form.Control value={classFilter} onChange={(e) => setClassFilter(e.target.value)} placeholder="e.g., 10" />
                </Col>
                <Col md={6}>
                  <Form.Label className="text-muted">Section (filter)</Form.Label>
                  <Form.Control value={sectionFilter} onChange={(e) => setSectionFilter(e.target.value)} placeholder="e.g., A" />
                </Col>
              </Row>

              <div className="mt-2" />
              <StudentLookup
                schoolId={schoolId}
                classFilter={classFilter}
                sectionFilter={sectionFilter}
                onPick={(st) => {
                  setStudent({
                    id: st.id,
                    name: st.name || st.displayName || "",
                    className: st.className || "",
                    section: st.section || "",
                    rollNo: st.rollNo || "",
                    phone: st.phone || "",
                    email: st.email || "",
                  });
                }}
              />

              {/* Student card */}
              {student ? (
                <Card className="border mt-3">
                  <Card.Body className="py-2">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <div className="fw-semibold">
                          {student.name}{" "}
                          <Badge bg="light" text="dark">Roll {student.rollNo || "—"}</Badge>
                        </div>
                        <div className="small text-muted">
                          Class {student.className || "—"}{student.section ? `-${student.section}` : ""} • {student.phone || student.email || "—"}
                        </div>
                      </div>
                      <Badge bg="secondary" title="Student selected">Selected</Badge>
                    </div>
                  </Card.Body>
                </Card>
              ) : (
                <Alert variant="light" className="mt-3 mb-0 border">Pick a student to see dues.</Alert>
              )}

              {/* Dues table */}
              <div className="mt-3" />
              <div className="d-flex justify-content-between align-items-center mb-1">
                <div className="fw-semibold">Dues</div>
                <div className="d-flex gap-2">
                  <Button size="sm" variant="outline-secondary" onClick={clearSelection}>Clear</Button>
                  <Button size="sm" variant="outline-primary" onClick={setAllSelectable}>Select all with balance</Button>
                </div>
              </div>

              <div style={{ maxHeight: 320, overflowY: "auto" }} className="border rounded">
                {loadingDues ? (
                  <div className="text-center text-muted py-4">
                    <Spinner size="sm" animation="border" /> Loading dues…
                  </div>
                ) : !student ? (
                  <div className="text-center text-muted py-4">No student selected.</div>
                ) : dues.length === 0 ? (
                  <div className="text-center text-muted py-4">No dues for this student.</div>
                ) : (
                  <Table hover responsive className="mb-0">
                    <thead className="table-light">
                      <tr>
                        <th style={{ width: 36 }}></th>
                        <th>Category</th>
                        <th className="text-end">Amount</th>
                        <th className="text-end">Paid</th>
                        <th className="text-end">Balance</th>
                        <th>Due Date</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dues.map((d) => {
                        const amt = Number(d.amount || 0);
                        const paid = Number(d.paidAmount || 0);
                        const bal = Math.max(0, amt - paid);
                        const checked = selectedIds.has(d.id);
                        const badge = d.status === "paid" ? "success" : d.status === "partial" ? "warning" : "secondary";
                        const dd = d.dueDate?.toDate ? new Date(d.dueDate.toDate()).toLocaleDateString() : d.dueDate || "—";
                        return (
                          <tr key={d.id} className={bal <= 0 ? "table-success" : ""}>
                            <td>
                              <Form.Check
                                type="checkbox"
                                checked={checked}
                                disabled={bal <= 0}
                                onChange={(e) => {
                                  const next = new Set(selectedIds);
                                  if (e.target.checked) next.add(d.id);
                                  else next.delete(d.id);
                                  setSelectedIds(next);
                                }}
                              />
                            </td>
                            <td className="text-truncate" style={{ maxWidth: 160 }}>
                              {d.categoryName || d.categoryId || "—"}
                            </td>
                            <td className="text-end">{fmtINR(amt)}</td>
                            <td className="text-end">{fmtINR(paid)}</td>
                            <td className={`text-end ${bal > 0 ? "text-danger fw-semibold" : ""}`}>
                              {fmtINR(bal)}
                            </td>
                            <td>{dd}</td>
                            <td><Badge bg={badge}>{d.status || "unpaid"}</Badge></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                )}
              </div>

              {/* Totals strip */}
              <div className="d-flex flex-wrap gap-3 mt-3 small text-muted">
                <div>Total Due (all): <b>{fmtINR(totals.totalAmt)}</b></div>
                <div>Total Paid (all): <b>{fmtINR(totals.totalPaid)}</b></div>
                <div>Balance (selected): <b>{fmtINR(totals.totalBalSel)}</b></div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* RIGHT: Payment Form */}
        <Col lg={6}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Header className="bg-white border-0 fw-semibold">Collect Payment</Card.Header>
            <Card.Body>
              {!student ? (
                <Alert variant="light" className="border">Select a student and dues to enable payment.</Alert>
              ) : (
                <>
                  <Row className="g-2">
                    <Col md={6}>
                      <Form.Group className="mb-2">
                        <Form.Label>Amount (₹)</Form.Label>
                        <Form.Control type="number" inputMode="decimal"
                          value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Amount received" />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <div className="mb-2 d-flex align-items-end gap-2" style={{ height: "100%" }}>
                        <Button size="sm" variant="outline-secondary" onClick={() => quickSet("half")}>Half of selected</Button>
                        <Button size="sm" variant="outline-primary" onClick={() => quickSet("full")}>Full selected</Button>
                        <Button size="sm" variant="outline-danger" onClick={() => quickSet("zero")}>Clear</Button>
                      </div>
                    </Col>
                  </Row>

                  <Row className="g-2">
                    <Col md={6}>
                      <Form.Group className="mb-2">
                        <Form.Label>Discount (₹)</Form.Label>
                        <Form.Control type="number" inputMode="decimal"
                          value={discount} onChange={(e) => setDiscount(e.target.value)} placeholder="0" />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-2">
                        <Form.Label>Late Fee (₹)</Form.Label>
                        <Form.Control type="number" inputMode="decimal"
                          value={lateFee} onChange={(e) => setLateFee(e.target.value)} placeholder="0" />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row className="g-2">
                    <Col md={6}>
                      <Form.Group className="mb-2">
                        <Form.Label>Mode</Form.Label>
                        <Form.Select value={mode} onChange={(e) => setMode(e.target.value)}>
                          <option value="cash">Cash</option>
                          <option value="upi">UPI</option>
                          <option value="card">Card</option>
                          <option value="bank">Bank Transfer</option>
                          <option value="cheque">Cheque</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      {(mode === "upi" || mode === "card" || mode === "bank") ? (
                        <Form.Group className="mb-2">
                          <Form.Label>Txn Ref / UTR</Form.Label>
                          <Form.Control value={txnRef} onChange={(e) => setTxnRef(e.target.value)} />
                        </Form.Group>
                      ) : mode === "cheque" ? (
                        <Form.Group className="mb-2">
                          <Form.Label>Cheque No</Form.Label>
                          <Form.Control value={chequeNo} onChange={(e) => setChequeNo(e.target.value)} />
                        </Form.Group>
                      ) : <div className="mb-2" />}
                    </Col>
                  </Row>

                  {(mode === "bank" || mode === "cheque") && (
                    <Row className="g-2">
                      <Col md={12}>
                        <Form.Group className="mb-2">
                          <Form.Label>Bank (optional)</Form.Label>
                          <Form.Control value={bank} onChange={(e) => setBank(e.target.value)} />
                        </Form.Group>
                      </Col>
                    </Row>
                  )}

                  <Form.Group className="mb-2">
                    <Form.Label>Notes (optional)</Form.Label>
                    <Form.Control value={note} onChange={(e) => setNote(e.target.value)} placeholder="Any note for this collection" />
                  </Form.Group>

                  <Form.Check className="mb-3" type="switch" id="auto-allocate"
                    label="Auto-allocate oldest dues first" checked={autoAllocate} onChange={(e) => setAutoAllocate(e.target.checked)} disabled />

                  <Alert variant="light" className="border">
                    <div className="small mb-1">Preview</div>
                    <div className="d-flex flex-wrap gap-3 small">
                      <div>Selected Balance: <b>{fmtINR(totals.totalBalSel)}</b></div>
                      <div>Gross: <b>{fmtINR(Number(amount || 0))}</b></div>
                      <div>+ Late Fee: <b>{fmtINR(Number(lateFee || 0))}</b></div>
                      <div>− Discount: <b>{fmtINR(Number(discount || 0))}</b></div>
                      <div>= Net: <b>{fmtINR(totals.net)}</b></div>
                    </div>
                    <div className="text-muted mt-1">Net will be allocated oldest-first to selected dues.</div>
                  </Alert>
                </>
              )}
            </Card.Body>

            <Card.Footer className="bg-white d-flex justify-content-end gap-2">
              <Button
                variant="outline-secondary"
                onClick={() => { setAmount(""); setDiscount(""); setLateFee(""); setTxnRef(""); setBank(""); setChequeNo(""); setNote(""); }}
                disabled={!student}
              >
                Reset
              </Button>
              <Button onClick={onCollect} disabled={!student || submitting || selectedIds.size === 0}>
                {submitting ? <Spinner size="sm" animation="border" /> : "Collect & Save"}
              </Button>
            </Card.Footer>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}
